// Legal documents for CalcGlobe. All documents follow the same structure so the
// shared <LegalPage /> component can render them generically.
// Last reviewed: 30 April 2026.

const CONTACT_EMAIL = 'support@calcglobe.com';
const SITE = 'CalcGlobe (https://calcglobe.com)';
const EFFECTIVE_DATE = '30 April 2026';

export const legalDocs = {
  'privacy-policy': {
    title: 'Privacy Policy',
    description: `Privacy Policy for ${SITE} — what data we collect, how we use it, and your rights.`,
    intro: `This Privacy Policy explains how CalcGlobe ("we", "us", "our") handles information when you use ${SITE} (the "Service"). We have designed CalcGlobe to be a privacy-respecting tool — most of what you do happens in your browser and never touches our servers.`,
    sections: [
      {
        h: 'Information we collect',
        p: [
          'CalcGlobe does not require an account, and we do not knowingly collect personally identifiable information (name, email, phone, address) from visitors of the calculator pages.',
          'We collect the minimum data needed to run the site, comply with the law, and improve the product:',
        ],
        list: [
          'Usage analytics — aggregated, anonymised page-view counts and approximate region. No individual user is identified.',
          'Technical logs — IP address, browser user-agent, and request timestamps are temporarily logged by our hosting provider for security and abuse prevention. Logs are retained for up to 30 days.',
          'Local browser storage — your calculation history, theme preference, and saved inputs are stored in your browser\'s localStorage. They never leave your device.',
          'Contact form (if used) — your email address and message are forwarded to us so we can respond. We do not add you to a mailing list.',
        ],
      },
      {
        h: 'How we use information',
        p: [
          'We use the limited information described above to: operate and maintain the Service; respond to support requests; detect and prevent abuse, spam, or security incidents; understand which calculators are most useful so we can improve them; and comply with legal obligations.',
        ],
      },
      {
        h: 'Cookies and third-party services',
        p: [
          'CalcGlobe uses essential cookies that are required for the site to function (for example, to remember your theme choice and consent state). With your consent we may also enable optional cookies for analytics and advertising.',
          'We may integrate the following third-party services. Each operates under its own privacy policy:',
        ],
        list: [
          'Google AdSense — for displaying advertising, subject to AdSense privacy controls (https://policies.google.com/technologies/ads).',
          'Google Analytics or a comparable analytics service — for aggregated traffic measurement.',
          'ExchangeRate-API and Frankfurter / European Central Bank — for currency reference data requested through our server-side proxy.',
          'Netlify / our hosting provider — for serving the site over HTTPS, with standard server-side logs.',
        ],
        pAfter: [
          'See our Cookie Policy for a full breakdown and instructions to manage your preferences.',
        ],
      },
      {
        h: 'Data retention',
        p: [
          'Server logs are retained for up to 30 days. Contact-form messages are retained only for as long as needed to respond to your query, then deleted within 12 months. Anonymous analytics are retained for up to 26 months in line with standard analytics defaults.',
        ],
      },
      {
        h: 'Your rights',
        p: [
          'Under the UK GDPR, EU GDPR, India\'s Digital Personal Data Protection Act, and similar laws, you have rights including: the right to access personal data we hold about you, the right to rectify inaccurate data, the right to erasure ("right to be forgotten"), the right to restrict or object to processing, and the right to data portability.',
          `To exercise any of these rights, contact us at ${CONTACT_EMAIL}. We will respond within 30 days.`,
        ],
      },
      {
        h: 'Children',
        p: [
          'CalcGlobe is a general-audience educational tool. We do not knowingly collect data from children under 13 (or under 16 in the EU). If you believe a child has provided us with information, please contact us and we will delete it promptly.',
        ],
      },
      {
        h: 'Changes to this policy',
        p: [
          `We may update this Privacy Policy from time to time. The "Effective date" at the top will reflect the most recent revision. Material changes will be highlighted on the homepage for at least 30 days.`,
        ],
      },
      {
        h: 'Contact us',
        p: [
          `Questions or concerns about this Privacy Policy? Email us at ${CONTACT_EMAIL}.`,
        ],
      },
    ],
  },

  'terms': {
    title: 'Terms of Use',
    description: `Terms of Use for ${SITE} — the agreement between you and CalcGlobe when you use the site.`,
    intro: `These Terms of Use ("Terms") govern your access to and use of ${SITE} (the "Service"). By using the Service you agree to these Terms. If you do not agree, please do not use CalcGlobe.`,
    sections: [
      {
        h: 'The service',
        p: [
          'CalcGlobe provides a collection of free, browser-based calculators and reference tables across finance, construction, health, engineering, medical, and science domains. The Service is provided free of charge for personal, educational, and professional reference use.',
        ],
      },
      {
        h: 'Acceptable use',
        p: [
          'You agree not to:',
        ],
        list: [
          'Use the Service for any unlawful purpose or in violation of any applicable regulation.',
          'Attempt to gain unauthorised access to any part of the Service, our servers, or any connected system.',
          'Scrape, crawl, or systematically download large portions of the site without our written permission.',
          'Reverse engineer, decompile, or attempt to extract the source code of any part of the Service beyond what is published openly.',
          'Use automated tools (bots, scrapers, headless browsers) at a rate that interferes with normal user experience for others.',
          'Misrepresent the Service\'s output as authoritative professional advice (see "Disclaimer of warranties" below).',
        ],
      },
      {
        h: 'Intellectual property',
        p: [
          'All content on CalcGlobe — including text, code, design, calculator logic, written articles, and reference tables — is the property of CalcGlobe or its licensors and is protected by copyright, trademark, and other intellectual-property laws.',
          'You are granted a limited, non-exclusive, revocable licence to access and use the Service for personal and professional reference. You may save, print, or share a calculation result for non-commercial use. You may not republish substantial portions of the site or pass off our content as your own.',
        ],
      },
      {
        h: 'Disclaimer of warranties',
        p: [
          'CalcGlobe is provided "as is" and "as available" with no warranties of any kind, express or implied. We make no warranty that the Service will be uninterrupted, secure, error-free, or accurate.',
          'Calculator outputs are computed from the inputs you provide and the formulas published on each page. They are intended for educational and reference purposes only and do not constitute financial, medical, legal, structural-engineering, or other professional advice. You are solely responsible for verifying any result before relying on it for a real-world decision. See our Disclaimer page for further detail.',
        ],
      },
      {
        h: 'Limitation of liability',
        p: [
          'To the maximum extent permitted by law, CalcGlobe, its operators, contributors, and affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages — including but not limited to loss of profits, revenue, data, goodwill, or other intangible losses — arising out of or in connection with your use of the Service.',
          'In jurisdictions that do not allow the exclusion or limitation of certain warranties or liabilities, the limitations above will apply only to the maximum extent permitted by applicable law.',
        ],
      },
      {
        h: 'Third-party links and integrations',
        p: [
          'The Service may contain links to third-party websites or services that are not owned or controlled by CalcGlobe. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party websites or services. You acknowledge and agree that CalcGlobe shall not be responsible or liable for any damage or loss caused by your use of any such third-party content.',
        ],
      },
      {
        h: 'Changes to the service or these terms',
        p: [
          'We may modify, suspend, or discontinue any part of the Service at any time without notice. We may also revise these Terms from time to time. Continued use of the Service after a revision becomes effective constitutes acceptance of the revised Terms.',
        ],
      },
      {
        h: 'Governing law',
        p: [
          'These Terms are governed by and construed in accordance with the laws applicable in the jurisdiction where the operator of CalcGlobe is established, without regard to its conflict-of-law provisions. Any dispute arising from these Terms or your use of the Service shall be resolved in the competent courts of that jurisdiction.',
        ],
      },
      {
        h: 'Contact us',
        p: [
          `Questions about these Terms? Email ${CONTACT_EMAIL}.`,
        ],
      },
    ],
  },

  'disclaimer': {
    title: 'Disclaimer',
    description: `Disclaimer for ${SITE} — calculator results are for informational and educational purposes only.`,
    intro: `CalcGlobe is a free educational tool. Despite our best efforts to ensure accuracy, the results produced by any calculator on this site are provided strictly for general informational and educational purposes. They are not a substitute for advice from a qualified professional in the relevant field.`,
    sections: [
      {
        h: 'Not professional advice',
        p: [
          'Information and calculations on CalcGlobe must not be interpreted as professional advice. In particular:',
        ],
        list: [
          'Financial calculators (EMI, SIP, mortgage, tax, ROI, inflation, PPF, salary, GST) provide indicative figures only. They do not account for personal circumstances, fees, taxes, or product-specific terms. Consult a qualified financial advisor, chartered accountant, or your bank before making any financial decision.',
          'Medical references (drug dosage, IV cannula, ET tube, BP, suture, catheter, needle sizes) summarise commonly published clinical data. They are intended for trained healthcare professionals as a quick reference only. Always verify against your local protocols, official drug formularies, and patient-specific factors. Do not use this site for self-diagnosis or self-treatment.',
          'Engineering and construction calculators (concrete, rebar, beam, pipe, bolt, electrical) provide first-pass estimates. They do not replace a qualified engineer\'s detailed design, structural analysis, safety factor selection, or local building-code compliance review.',
          'Health & fitness calculators (BMR/TDEE, BMI, body fat, VO2 max, heart rate zones) use population-average formulas. Individual results vary significantly. Consult a doctor, dietitian, or qualified trainer before starting any new diet or training program.',
          'Science and math calculators are intended for educational use. While we test for accuracy, edge cases or unusual inputs may produce incorrect results. Verify critical computations independently.',
        ],
      },
      {
        h: 'No warranty of accuracy',
        p: [
          'Although CalcGlobe is built with care, no software is bug-free. We make no warranty that the calculations or content on this site are complete, accurate, current, or fit for any particular purpose. The information and formulas may change without notice.',
        ],
      },
      {
        h: 'External links',
        p: [
          'CalcGlobe may link to external websites for reference. We do not endorse, verify, or accept responsibility for content on external sites. Use external sources at your own discretion.',
        ],
      },
      {
        h: 'Affiliate and advertising disclosure',
        p: [
          'CalcGlobe may display advertisements served by Google AdSense or other ad networks. Ads are clearly labelled. We do not endorse advertised products and we have no editorial control over ad content. We may receive a small commission if you click on an ad or follow an affiliate link, at no extra cost to you. Advertising revenue helps keep CalcGlobe free.',
        ],
      },
      {
        h: 'Use at your own risk',
        p: [
          'By using CalcGlobe you accept that you do so at your own risk. We accept no liability for any loss, injury, claim, or damage arising from your reliance on any information or calculation provided through the Service.',
        ],
      },
      {
        h: 'Contact us',
        p: [
          `If you notice an error in any calculator or article, please let us know at ${CONTACT_EMAIL} so we can investigate and correct it.`,
        ],
      },
    ],
  },

  'cookie-policy': {
    title: 'Cookie Policy',
    description: `Cookie Policy for ${SITE} — what cookies we use, why, and how to manage your preferences.`,
    intro: `This Cookie Policy explains what cookies are, which cookies CalcGlobe uses, and how you can control them. Please read this in conjunction with our Privacy Policy.`,
    sections: [
      {
        h: 'What is a cookie?',
        p: [
          'A cookie is a small text file placed on your device (computer, tablet, phone) when you visit a website. Cookies are widely used to make websites work efficiently and to provide information to site operators. Modern browsers also support similar technologies — localStorage, sessionStorage, IndexedDB — which we group together as "cookies and similar technologies" for the purposes of this policy.',
        ],
      },
      {
        h: 'Categories of cookies we use',
        p: [
          'CalcGlobe uses cookies and localStorage in the following categories:',
        ],
        list: [
          'Strictly necessary — required for the site to function. Includes your consent choice ("calcglobe-cookie-consent") and theme preference ("theme"). These cookies cannot be disabled because the site would not work without them.',
          'Functional — remember your preferences so the site works the way you expect on return visits. Includes calculation history ("calcglobe-history") stored in localStorage. Stored only in your browser; we never see it.',
          'Analytics — help us understand which calculators are most used, so we can prioritise improvements. Anonymous and aggregated. Disabled unless you opt in.',
          'Advertising — set by Google AdSense (or equivalent ad partner) to show relevant advertisements and measure ad performance. Disabled unless you opt in. Subject to the ad network\'s own privacy policy.',
        ],
      },
      {
        h: 'Third-party cookies',
        p: [
          'When we use Google AdSense, Google Analytics, or a similar third-party service, those providers may set their own cookies. We do not have direct control over these cookies — they are governed by the third party\'s privacy and cookie policies. Where consent is required, we will not enable them until you have given consent.',
        ],
      },
      {
        h: 'Managing cookies',
        p: [
          'You can manage your cookie preferences in three ways:',
        ],
        list: [
          'Use the CalcGlobe cookie banner that appears on your first visit. You can click "Manage" later by visiting this page (link below) to update your choice.',
          'Use your browser settings to delete existing cookies and block future ones. Note that blocking strictly-necessary cookies will prevent CalcGlobe from working correctly.',
          'For advertising cookies specifically, you can opt out at the Network Advertising Initiative (https://optout.networkadvertising.org), the Digital Advertising Alliance (https://optout.aboutads.info), or via Google\'s ads-settings page (https://adssettings.google.com).',
        ],
      },
      {
        h: 'Changes to this Cookie Policy',
        p: [
          'We may update this Cookie Policy from time to time. The effective date at the top reflects the most recent revision. Material changes will be communicated on the homepage.',
        ],
      },
      {
        h: 'Contact us',
        p: [
          `For questions about cookies or to exercise your data rights, email ${CONTACT_EMAIL}.`,
        ],
      },
    ],
  },
  'editorial-standards': {
    title: 'Editorial Standards & Methodology',
    description:
      'How CalcGlobe selects formulas, sources reference data, verifies calculations, and reviews its content — including our approach to medical and financial information.',
    intro:
      'CalcGlobe publishes calculators and reference tables that people use to make real decisions — sizing a cannula, ordering concrete, or working out what a loan will actually cost. That carries an obligation. This page sets out where our numbers come from, how they are checked, what we will and will not claim, and how to tell us when we get something wrong.',
    sections: [
      {
        h: 'Who publishes CalcGlobe',
        p: [
          'CalcGlobe is built and maintained by Palash Shirurkar, based in Brisbane, Australia.',
          'Madhura brings a professional background in pharmaceutical quality assurance and regulatory affairs, with experience in documentation, quality processes and regulatory requirements.',
          'CalcGlobe is an independent project. It is not affiliated with any standards body, manufacturer, lender, or healthcare organisation, and no company pays for placement, endorsement, or favourable treatment in any calculator or reference table.',
        ],
      },
      {
        h: 'How formulas are selected',
        p: [
          'Our editorial goal is to identify the formula and assumptions behind each result. Where multiple conventions exist, the selected method should be stated. A formula name is not evidence that all implementations or edge cases have been independently verified.',
        ],
        list: [
          'Finance — standard amortisation and compounding formulas, stated in full on each page so the arithmetic can be checked by hand.',
          'Health — established equations identified by name (for example Mifflin-St Jeor for basal metabolic rate), with their known limitations stated rather than omitted.',
          'Engineering and medical reference — identify the applicable standard or manufacturer source, the covered sizes and any limitations. A subset must not be described as a complete standard.',
          'Physics, chemistry and mathematics — textbook relationships, shown with their working so a student can follow the derivation rather than trust a black box.',
        ],
      },
      {
        h: 'How reference data is sourced',
        p: [
          'Reference pages discuss standards and manufacturer information relevant to their subject. Coverage and verification are incomplete. Before using a dimension, rating or clinical reference, confirm the applicable edition, product specification and context against the original source. Naming a standard does not establish that every table row has been verified against it.',
          'Standards and guidance change. Check the source edition and review date where provided; do not assume that a page reflects the latest revision solely because it remains online.',
        ],
      },
      {
        h: 'How calculations are verified',
        p: [
          'Verification is ongoing. A worked example explains a method, but does not by itself establish that every input or reference-table row has been independently validated. Checks should compare results with an independent source and cover units, rounding and invalid inputs. We do not claim that all tools have completed this review.',
        ],
      },
      {
        h: 'Medical and health content',
        p: [
          // ⚠️ EDIT IF NEEDED — but do NOT add "medically reviewed by a clinician"
          // unless a named, qualified clinician has genuinely reviewed the page.
          // Claiming review you do not have is a false trust signal.
          'Medical reference material on CalcGlobe is compiled from published standards and current professional guidance, with the relevant standard or body identified on the page. It is prepared with care, but it is not written or reviewed by a practising clinician, and we state that plainly rather than implying a clinical endorsement that does not exist.',
          'These pages are educational reference for people who already work in a clinical setting and who are expected to apply their own training and local protocol. They are not medical advice, not a substitute for clinical judgement, and not a basis for treating anyone. Local policy overrides any general guidance published here. If a value here conflicts with your organisation\'s protocol, follow your protocol and please tell us.',
        ],
      },
      {
        h: 'Financial content',
        p: [
          'Financial calculators produce estimates from the figures you enter and the assumptions stated on the page. They are not quotes, offers, or projections of an actual outcome. Lenders apply their own rounding, fees, and compounding conventions, so a real repayment schedule will differ from a generic calculation.',
          'Where a result depends on an assumed rate of return, that assumption is displayed and is not presented as a guarantee. Tax rules, contribution limits, and statutory rates change and vary by jurisdiction; figures are correct to the review date shown and should be confirmed against the current rules that apply to you. Nothing on CalcGlobe is personal financial advice.',
        ],
      },
      {
        h: 'Review and updates',
        p: [
          'We prioritise reported discrepancies and updates affecting medical reference, engineering and finance. Review coverage is incomplete; publication does not imply continuous monitoring of every standard or regulation.',
          'When reporting a discrepancy, please include the page URL, inputs, displayed result and the source you used for comparison. This helps us reproduce and assess the issue.',
        ],
      },
      {
        h: 'Reporting an error',
        p: [
          'If a figure here looks wrong, please tell us — corrections from people who use these tables in practice are the most valuable feedback we receive, and they are acted on.',
          'Email support@calcglobe.com with the page, the value you believe is incorrect, and the correct value with its source if you have it. Reports touching medical or engineering reference data are prioritised.',
        ],
      },
      {
        h: 'Independence',
        p: [
          'CalcGlobe is free to use and carries no paywall or account requirement. The site may display advertising to cover hosting costs. Advertising is served by third parties and has no influence over which calculators exist, what the reference tables say, or how any result is presented. No advertiser reviews content before publication.',
        ],
      },
    ],
  },

  
};

export const legalMeta = {
  effectiveDate: EFFECTIVE_DATE,
  contactEmail: CONTACT_EMAIL,
};
